/**
 * Catalog of TTS models the user can download / select.
 * Only MIT / Apache-2.0 (and browser Web Speech) — no CC-BY-NC.
 */

import { HardwareTier } from './hardware';

export type EngineKind = 'webspeech' | 'piper' | 'kokoro' | 'chatterbox' | 'vieneu';

export type ModelEntry = {
  id: string;
  label: string;
  subtitle: string;
  engine: EngineKind;
  langs: string[];
  cloning: boolean;
  /** Approx download size in MB */
  sizeMb: number;
  license: string;
  /** HuggingFace repo or CDN path key */
  repo: string;
  dtype?: string;
  minTier: HardwareTier;
  /** Needs explicit consent before download (large) */
  needsConsent?: boolean;
  /** Coming soon placeholder */
  comingSoon?: boolean;
  recommended?: boolean;
};

export const MODELS: ModelEntry[] = [
  {
    id: 'webspeech',
    label: 'Giọng trình duyệt',
    subtitle: 'Web Speech API — 0MB, EN + VI tùy hệ điều hành',
    engine: 'webspeech',
    langs: ['en', 'vi'],
    cloning: false,
    sizeMb: 0,
    license: 'Browser',
    repo: 'webspeech',
    minTier: 'low',
    recommended: true,
  },
  {
    id: 'piper-vi-vivos',
    label: 'Piper tiếng Việt (nhẹ)',
    subtitle: 'vi_VN vivos · ~14MB · WASM · MIT',
    engine: 'piper',
    langs: ['vi'],
    cloning: false,
    sizeMb: 14,
    license: 'MIT',
    repo: 'piper-vi_VN-vivos-x_low',
    dtype: 'int8',
    minTier: 'low',
  },
  {
    id: 'piper-vi-vais',
    label: 'Piper tiếng Việt (chuẩn)',
    subtitle: 'vi_VN vais1000 medium · ~64MB · MIT',
    engine: 'piper',
    langs: ['vi'],
    cloning: false,
    sizeMb: 64,
    license: 'MIT',
    repo: 'piper-vi_VN-vais1000-medium',
    dtype: 'fp32',
    minTier: 'low',
  },
  {
    id: 'piper-en-lessac',
    label: 'Piper English (Lessac)',
    subtitle: 'en_US lessac medium · ~60MB · MIT',
    engine: 'piper',
    langs: ['en'],
    cloning: false,
    sizeMb: 60,
    license: 'MIT',
    repo: 'piper-en_US-lessac-medium',
    dtype: 'fp32',
    minTier: 'low',
  },
  {
    id: 'kokoro-82m',
    label: 'Kokoro 82M',
    subtitle: '29 giọng tiếng Anh · ~86MB q8 · Apache-2.0 · có cảm xúc nhẹ',
    engine: 'kokoro',
    langs: ['en'],
    cloning: false,
    sizeMb: 86,
    license: 'Apache-2.0',
    repo: 'onnx-community/Kokoro-82M-v1.0-ONNX',
    dtype: 'q8',
    minTier: 'mid',
    recommended: true,
  },
  {
    id: 'vieneu-v3-turbo',
    label: 'VieNeu-TTS v3 Turbo (VI + clone)',
    subtitle: 'Clone + TTS tiếng Việt · ~280MB int8 · Apache-2.0 · CPU/WASM · Beta',
    engine: 'vieneu',
    langs: ['vi', 'en'],
    cloning: true,
    sizeMb: 280,
    license: 'Apache-2.0',
    repo: 'pnnbao-ump/VieNeu-TTS-v3-Turbo',
    dtype: 'int8',
    minTier: 'mid',
    needsConsent: true,
    recommended: true,
  },
  {
    id: 'chatterbox-en',
    label: 'Chatterbox — Clone voice (EN)',
    subtitle: 'Zero-shot clone từ mẫu 5–30s · ~1.5GB · MIT · WebGPU',
    engine: 'chatterbox',
    langs: ['en'],
    cloning: true,
    sizeMb: 1500,
    license: 'MIT',
    repo: 'onnx-community/chatterbox-ONNX',
    dtype: 'q4f16',
    minTier: 'high',
    needsConsent: true,
  },
];

export function getModelById(id: string): ModelEntry | undefined {
  return MODELS.find((m) => m.id === id);
}
